import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { cn } from '@helios/ui';

/**
 * Single-viewport dashboard contract:
 * - Fits within the app shell (no page-level vertical scroll).
 * - Overflow content is reached via a top horizontal tab strip.
 * - Active panel may scroll internally only when a subsection cannot densify further.
 * - Short viewports retain a usable panel height; the owning DensePagePanel scrolls
 *   the dashboard chrome and content together instead of collapsing the workspace.
 *
 * URL: `?view=<tabId>` (optional). Falls back to `defaultTabId` / first tab.
 */

export interface DashboardTab {
  id: string;
  label: string;
  content: ReactNode;
  /** Optional count badge in the tab label. */
  count?: number;
}

export interface SingleScreenDashboardProps {
  /** Optional page header; omit when the parent already renders chrome. */
  header?: ReactNode;
  tabs: DashboardTab[];
  defaultTabId?: string;
  /** Query param key for the active tab. Defaults to `view`. */
  paramKey?: string;
  /** Persist tab in the URL. Defaults to true. */
  syncUrl?: boolean;
  /** Controlled tab selection; supply onTabChange to handle navigation. */
  activeTabId?: string;
  onTabChange?: (id: string) => void;
  className?: string;
  /** Allows a viewport-filling workspace to allocate its own panel scrolling. */
  panelClassName?: string;
  /** Extra chrome between header and tabs (filters, banners). */
  toolbar?: ReactNode;
}

export function DashboardTabs({
  tabs,
  activeId,
  onChange,
  className,
}: {
  tabs: Array<Pick<DashboardTab, 'id' | 'label' | 'count'>>;
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label="Dashboard sections"
      onKeyDown={(event) => {
        const controls = Array.from(
          event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
        );
        const current = controls.indexOf(event.target as HTMLButtonElement);
        if (current < 0 || controls.length === 0) return;
        const next =
          event.key === 'ArrowRight'
            ? (current + 1) % controls.length
            : event.key === 'ArrowLeft'
              ? (current - 1 + controls.length) % controls.length
              : event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? controls.length - 1
                  : null;
        if (next === null) return;
        event.preventDefault();
        const tab = tabs[next];
        if (!tab) return;
        onChange(tab.id);
        controls[next]?.focus();
      }}
      className={cn('border-border flex shrink-0 gap-1 overflow-x-auto border-b pb-px', className)}
    >
      {tabs.map((tab) => {
        const selected = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`dashboard-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`dashboard-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              'hx-section-tab relative whitespace-nowrap rounded-t-md px-3 py-1.5 text-xs font-semibold tracking-wide transition-colors',
              'focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2',
              selected
                ? 'bg-card text-context-primary after:bg-context-primary after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full'
                : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
            )}
          >
            {tab.label}
            {typeof tab.count === 'number' ? (
              <span
                className={cn(
                  'text-2xs ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-sm px-1 tabular-nums',
                  selected
                    ? 'bg-context-primary/15 text-context-primary'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function SingleScreenDashboard({
  header,
  tabs,
  defaultTabId,
  paramKey = 'view',
  syncUrl = true,
  activeTabId,
  onTabChange,
  className,
  panelClassName,
  toolbar,
}: SingleScreenDashboardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const fallbackId =
    defaultTabId && tabIds.includes(defaultTabId) ? defaultTabId : (tabIds[0] ?? '');

  const urlTab = syncUrl ? searchParams.get(paramKey) : null;
  const initialId = urlTab && tabIds.includes(urlTab) ? urlTab : fallbackId;
  const [localActiveId, setActiveId] = useState(initialId);
  const activeId = activeTabId ?? localActiveId;
  const controlled = activeTabId !== undefined;

  useEffect(() => {
    if (!controlled && !tabIds.includes(activeId) && fallbackId) {
      setActiveId(fallbackId);
    }
  }, [activeId, controlled, fallbackId, tabIds]);

  useEffect(() => {
    if (controlled || !syncUrl || !activeId) return;
    const current = searchParams.get(paramKey);
    if (current === activeId) return;
    const next = new URLSearchParams(searchParams);
    next.set(paramKey, activeId);
    setSearchParams(next, { replace: true });
  }, [activeId, controlled, paramKey, searchParams, setSearchParams, syncUrl]);

  const activeTab = tabs.find((tab) => tab.id === activeId) ?? tabs[0];

  const selectTab = (id: string) => {
    if (!tabIds.includes(id)) return;
    if (controlled) {
      onTabChange?.(id);
      return;
    }
    setActiveId(id);
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-1 flex-col gap-2 overflow-hidden [@media(max-height:600px)]:min-h-[480px]',
        className,
      )}
    >
      {header ? <div className="shrink-0">{header}</div> : null}
      {toolbar ? <div className="shrink-0">{toolbar}</div> : null}
      <DashboardTabs tabs={tabs} activeId={activeTab?.id ?? fallbackId} onChange={selectTab} />
      <div
        role="tabpanel"
        id={activeTab ? `dashboard-panel-${activeTab.id}` : undefined}
        aria-labelledby={activeTab ? `dashboard-tab-${activeTab.id}` : undefined}
        className={cn('min-h-0 flex-1 overflow-auto pr-0.5', panelClassName)}
      >
        {activeTab?.content}
      </div>
    </div>
  );
}
